import React from "react";
import {
  FileText,
  Eye,
  Download,
  CheckCircle,
  Save,
  X,
} from "../../constants/icons";

  // ===== DOCUMENTS MODAL COMPONENT =====
  const DocumentsModal = ({
    selectedEmployeeDocuments = null,
    setShowDocumentsModal = () => {},
    setSelectedEmployeeDocuments = () => {},
    apiFetch = () => {},
    API_URL = "",
    showAlert = () => {},
    language = "en",
    styles: stylesProp = {},
    screenSize = "md",
    text = {},
    loadingDocuments = false,
    theme = "light",
    spacing = {},
    fonts = {},
    documentChecklist = {},
    handleDocumentCheckboxChange = () => {},
    handleSaveDocuments = () => {},
    hasUnsavedChanges = false,
    savingDocument = false,
  }) => {
    if (!selectedEmployeeDocuments) return null;

    const defaultStyles = {
      modal: {
        position: "fixed",
        inset: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        backdropFilter: "blur(3px)",
        animation: "fadeIn 300ms ease-out",
      },
      viewModalContent: {
        position: "relative",
        width: "92%",
        maxWidth: 900,
        maxHeight: "92vh",
        overflowY: "auto",
        background: theme === "dark"
          ? "linear-gradient(135deg, #0f172a 0%, #1a2a3a 100%)"
          : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        color: theme === "dark" ? "#e6eef8" : "#0f172a",
        borderRadius: 16,
        padding: "32px",
        boxShadow: theme === "dark"
          ? "0 25px 60px rgba(0,0,0,0.8), 0 0 1px rgba(255,255,255,0.1)"
          : "0 20px 50px rgba(0,0,0,0.12), 0 0 1px rgba(0,0,0,0.04)",
        border: theme === "dark"
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(148,163,184,0.15)",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        animation: "slideUp 400ms cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
      modalHeader: {
        marginBottom: 32,
        paddingBottom: 16,
        borderBottom: theme === "dark"
          ? "1px solid rgba(255,255,255,0.08)"
          : "1px solid rgba(0,0,0,0.08)",
        display: "flex",
        alignItems: "center",
        gap: 12,
      },
      modalTitle: {
        margin: 0,
        fontSize: 24,
        fontWeight: 800,
        background: theme === "dark"
          ? "linear-gradient(135deg, #fff 0%, #cbd5e1 100%)"
          : "linear-gradient(135deg, #0f172a 0%, #0f172a 100%)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
        letterSpacing: "-0.5px",
        display: "flex",
        alignItems: "center",
        gap: 10,
      },
      closeBtn: {
        position: "absolute",
        top: 14,
        right: language === "ar" ? "auto" : 14,
        left: language === "ar" ? 14 : "auto",
        width: 40,
        height: 40,
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: theme === "dark" ? "rgba(13,18,28,0.15)" : "rgba(255,255,255,0.92)",
        border: theme === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        cursor: "pointer",
        transition: "all 200ms ease",
        zIndex: 20,
        color: theme === "dark" ? "#94a3b8" : "#475569",
      },
      actionBtn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px 10px",
        borderRadius: 6,
        border: "1px solid rgba(255,255,255,0.1)",
        cursor: "pointer",
        fontSize: 13,
        transition: "all 150ms ease",
        background: "transparent",
        color: theme === "dark" ? "#94a3b8" : "#475569",
      },
      viewBtn: {
        background: "rgba(139,92,246,0.15)",
        borderColor: "rgba(139,92,246,0.3)",
        color: "#8b5cf6",
      },
      documentsBtn: {
        background: "rgba(59,130,246,0.15)",
        borderColor: "rgba(59,130,246,0.3)",
        color: "#3b82f6",
      },
    };

    const styles = { ...defaultStyles, ...(stylesProp || {}) };

    const handlePreview = async (documentId) => {
      try {
        // ✅ Use apiFetch with automatic token refresh
        const response = await apiFetch(`${API_URL}/api/documents/preview/${documentId}`, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error('Failed to preview document');
        }

        // Get the blob and open in new tab
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');

        // Clean up after a delay
        setTimeout(() => window.URL.revokeObjectURL(url), 100);
      } catch (err) {
        console.error('Preview error:', err);
        await showAlert({ message: language === "ar" ? 'فشل في عرض المستند' : 'Failed to preview document' });
      }
    };

    const handleDownload = async (documentId) => {
      try {
        // ✅ Use apiFetch with automatic token refresh
        const response = await apiFetch(`${API_URL}/api/documents/download/${documentId}`, {
          method: 'GET'
        });

        if (!response.ok) {
          throw new Error('Failed to download document');
        }

        const blob = await response.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuffer);

        // 1️⃣ Try filename from Content-Disposition
        let filename = null;
        const contentDisposition = response.headers.get('Content-Disposition');

        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/);
          if (match && match[1]) {
            filename = decodeURIComponent(match[1].replace(/"/g, ''));
          }
        }

        // 2️⃣ Infer extension from MIME type
        const mimeToExt = {
          'application/pdf': 'pdf',
          'application/msword': 'doc',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
          'application/vnd.ms-excel': 'xls',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
          'image/png': 'png',
          'image/jpeg': 'jpg',
          'image/webp': 'webp',
          'text/plain': 'txt',
          'application/zip': 'zip',
        };

        let extension = mimeToExt[blob.type];

        // 3️⃣ Magic bytes fallback (very important)
        if (!extension) {
          const hex = [...bytes.slice(0, 8)]
            .map(b => b.toString(16).padStart(2, '0'))
            .join('');

          if (hex.startsWith('25504446')) extension = 'pdf';
          else if (hex.startsWith('504b0304')) extension = 'zip'; // docx/xlsx/pptx
          else if (hex.startsWith('ffd8ff')) extension = 'jpg';
          else if (hex.startsWith('89504e47')) extension = 'png';
          else extension = 'bin';
        }

        // 4️⃣ Build filename safely
        if (!filename) {
          filename = `document.${extension}`;
        } else if (!filename.includes('.')) {
          filename = `${filename}.${extension}`;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;

        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        setTimeout(() => window.URL.revokeObjectURL(url), 200);
      } catch (err) {
        console.error('Download error:', err);
        await showAlert({ message: language === 'ar' ? 'فشل في تحميل المستند' : 'Failed to download document' });
      }
    };


    const handleModalClick = (e) => {
      if (e.target === e.currentTarget) {
        setShowDocumentsModal(false);
        setSelectedEmployeeDocuments(null);
      }
    };

    return (
      <div style={styles.modal} onClick={handleModalClick}>
        <div style={styles.viewModalContent}>
          {/* Close Button */}
          <button
            style={styles.closeBtn}
            onClick={() => { setShowDocumentsModal(false); setSelectedEmployeeDocuments(null); }}
            title={language === "ar" ? "إغلاق" : "Close"}
          >
            <X size={18} />
          </button>

          {/* Modal Header */}
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>
              <FileText size={screenSize === 'xs' ? 18 : 20} />
              {language === "ar" ? "مستندات " : "Documents - "}
              {(() => {
                const displayName = language === "ar"
                  ? (selectedEmployeeDocuments?.arabicName || selectedEmployeeDocuments?.name)
                  : selectedEmployeeDocuments?.name;

                return typeof displayName === "string" && displayName.trim()
                  ? displayName.trim().split(/\s+/).slice(0, 3).join(" ")
                  : text[language].na;
              })()}
            </h2>
          </div>

          {/* Loading State */}
          {loadingDocuments && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
            }}>
              {text[language].loading}
            </div>
          )}

          {/* Documents List */}
          {!loadingDocuments && selectedEmployeeDocuments.documents && (
            <div style={{ marginTop: spacing.margin }}>
              {selectedEmployeeDocuments.documents.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                }}>
                  {language === "ar" ? "لا توجد مستندات" : "No documents found"}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: screenSize === 'xs' ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
                  gap: spacing.gap,
                }}>
                  {selectedEmployeeDocuments.documents.map((doc) => {
                    const fileName = doc.filePath ? doc.filePath.split('/').pop() : 'unknown';
                    const fileExtension = fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : 'FILE';

                    return (
                      <div
                        key={doc.id}
                        style={{
                          background: theme === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)",
                          border: theme === "dark"
                            ? "1px solid rgba(255, 255, 255, 0.1)"
                            : "1px solid rgba(0, 0, 0, 0.1)",
                          borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
                          padding: spacing.padding,
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(255, 255, 255, 0.08)"
                            : "rgba(0, 0, 0, 0.05)";
                          e.currentTarget.style.transform = "translateY(-2px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = theme === "dark"
                            ? "rgba(255, 255, 255, 0.05)"
                            : "rgba(0, 0, 0, 0.03)";
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Document Icon */}
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(139, 92, 246, 0.1)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 1rem',
                        }}>
                          <FileText size={32} color="#8b5cf6" />
                        </div>

                        {/* Document Type */}
                        <div style={{
                          fontSize: fonts.body,
                          fontWeight: '600',
                          color: theme === "dark" ? '#fff' : '#1a1a1a',
                          marginBottom: '0.5rem',
                          textAlign: 'center',
                        }}>
                          {doc.type}
                        </div>

                        {/* File Extension */}
                        <div style={{
                          fontSize: fonts.small,
                          color: theme === "dark" ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                          marginBottom: '1rem',
                          textAlign: 'center',
                        }}>
                          {fileExtension}
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                          display: 'flex',
                          gap: '0.5rem',
                          justifyContent: 'center',
                        }}>
                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.viewBtn,
                              flex: 1,
                            }}
                            onClick={() => handlePreview(doc.id)}
                            title={language === "ar" ? "عرض" : "View"}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            style={{
                              ...styles.actionBtn,
                              ...styles.documentsBtn,
                              flex: 1,
                            }}
                            onClick={() => handleDownload(doc.id)}
                            title={language === "ar" ? "تحميل" : "Download"}
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Document Checklist (under real documents) */}
          {!loadingDocuments && (
            <div style={{ marginTop: spacing.margin }}>
              <div style={{
                color: theme === "dark" ? '#fff' : '#1a1a1a',
                fontSize: fonts.subtitle,
                fontWeight: '600',
                marginBottom: spacing.gap,
              }}>
                {text[language].documentChecklist}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
                gap: spacing.gap,
              }}>
                {[
                  ['photosCount', text[language].photos],
                  ['birthCertificateCopy', text[language].birthForm],
                  ['gradCertificateOriginal', text[language].graduation],
                  ['idCopy', text[language].idCopy],
                  ['noCrimeCertificate', text[language].criminalRecord],
                  ['laborLetterOriginal', text[language].workCertificate],
                  ['medicalCheckForm111', text[language].form111],
                ].map(([field, label]) => (
                  <label
                    key={field}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      padding: '1rem',
                      borderRadius: '8px',
                      backgroundColor: theme === "dark" ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${theme === "dark" ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(documentChecklist[field])}
                      onChange={(e) => handleDocumentCheckboxChange(field, e.target.checked)}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#10b981',
                      }}
                    />
                    <span style={{
                      fontSize: fonts.body,
                      color: theme === "dark" ? '#fff' : '#1f2937',
                      fontWeight: '500',
                      flex: 1,
                    }}>
                      {label}
                    </span>
                    {documentChecklist[field] && <CheckCircle size={18} style={{ color: '#10b981' }} />}
                  </label>
                ))}
              </div>

              <div style={{
                marginTop: spacing.gap,
                display: 'flex',
                justifyContent: 'flex-end',
              }}>
                <button
                  onClick={handleSaveDocuments}
                  disabled={!hasUnsavedChanges || savingDocument}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                    fontSize: fonts.body,
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                    opacity: savingDocument ? 0.7 : 1,
                  }}
                >
                  <Save size={18} />
                  {savingDocument
                    ? (language === 'ar' ? 'جاري الحفظ...' : 'Saving...')
                    : (language === 'ar' ? 'حفظ التغييرات' : 'Save Changes')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

export default DocumentsModal;

